import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WhatsappLogo, Phone, EnvelopeSimple, Sparkle, ArrowLeft, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FollowupDateTime, emptyDateTime, toIso, formatFollowup } from "@/components/FollowupDateTime";

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [newFuDate, setNewFuDate] = useState(emptyDateTime);
  const [newFuNotes, setNewFuNotes] = useState("");

  const load = async () => {
    const r = await api.get(`/leads/${id}`);
    setLead(r.data);
    const fu = await api.get("/followups", { params: { lead_id: id } });
    setFollowups(fu.data);
  };

  useEffect(() => { load(); }, [id]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const r = await api.post(`/ai/suggest-followup/${id}`);
      setSuggestion(r.data);
    } catch { toast.error("AI suggestion failed"); }
    finally { setSuggesting(false); }
  };

  const addFu = async (e) => {
    e.preventDefault();
    const scheduled_at = toIso(newFuDate);
    if (!scheduled_at) { toast.error("Please select a follow-up date"); return; }
    try {
      await api.post("/followups", { lead_id: id, scheduled_at, notes: newFuNotes });
      setNewFuDate(emptyDateTime); setNewFuNotes("");
      await load();
      toast.success("Follow-up scheduled");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save follow-up"); }
  };

  const complete = async (fid) => {
    await api.patch(`/followups/${fid}`, { completed: true });
    await load();
  };

  if (!lead) return <div className="text-slate-400">Loading…</div>;

  const waNum = (lead.whatsapp || lead.mobile || "").replace(/[^0-9]/g, "");

  return (
    <div className="space-y-6" data-testid="lead-detail-page">
      <Link to="/leads" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#D4AF37]">
        <ArrowLeft size={14} /> Back to Leads
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-luxury p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Lead Profile</div>
              <h1 className="font-serif text-3xl text-white mt-1">{lead.name}</h1>
              <div className="text-slate-400 text-sm mt-1">{lead.project || "No project assigned"} • {lead.location}</div>
            </div>
            <div className="flex gap-2">
              {waNum && <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center hover:bg-emerald-500/20"><WhatsappLogo size={18} weight="duotone" /></a>}
              <a href={`tel:${lead.mobile}`} className="w-10 h-10 rounded-md bg-sky-500/10 border border-sky-500/30 text-sky-400 flex items-center justify-center"><Phone size={18} weight="duotone" /></a>
              {lead.email && <a href={`mailto:${lead.email}`} className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center"><EnvelopeSimple size={18} weight="duotone" /></a>}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#1E293B]">
            {[
              ["Mobile", lead.mobile], ["Email", lead.email || "—"],
              ["Budget", lead.budget || "—"], ["Property Type", lead.property_type || "—"],
              ["Status", lead.status], ["Priority", lead.priority],
              ["Source", lead.source], ["Created", new Date(lead.created_at).toLocaleDateString()],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">{k}</div>
                <div className="text-sm text-white mt-1">{v}</div>
              </div>
            ))}
          </div>

          {lead.notes && (
            <div className="mt-6 pt-6 border-t border-[#1E293B]">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Notes</div>
              <p className="text-sm text-slate-300">{lead.notes}</p>
            </div>
          )}
        </div>

        <div className="card-luxury p-6 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2">
              <Sparkle className="text-[#D4AF37]" size={20} weight="duotone" />
              <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">AI Score</div>
            </div>
            {lead.ai_score ? (
              <>
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="font-serif text-5xl text-white">{lead.ai_score.score}</span>
                  <span className="text-[#D4AF37] uppercase tracking-widest text-xs">{lead.ai_score.tier}</span>
                </div>
                <p className="text-sm text-slate-300 mt-3">{lead.ai_score.reasoning}</p>
                <div className="mt-4 pt-4 border-t border-[#1E293B]">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Next Action</div>
                  <div className="text-sm text-white mt-1">{lead.ai_score.next_action}</div>
                </div>
              </>
            ) : (
              <div className="mt-3 text-sm text-slate-400">No AI score yet. Generate one to prioritize.</div>
            )}
            <button onClick={async () => { await api.post(`/ai/lead-score/${id}`); await load(); toast.success("Rescored"); }} data-testid="rescore-btn"
              className="mt-4 w-full py-2 text-xs uppercase tracking-widest rounded-md border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10">
              {lead.ai_score ? "Re-score" : "Generate AI Score"}
            </button>
            <button onClick={suggest} disabled={suggesting} data-testid="suggest-btn"
              className="mt-2 w-full py-2 text-xs uppercase tracking-widest rounded-md bg-[#D4AF37] text-[#060B14] font-semibold hover:bg-[#F2CE6E] disabled:opacity-60">
              {suggesting ? "Thinking…" : "Suggest Follow-up"}
            </button>
            {suggestion && (
              <div className="mt-4 p-3 rounded-md bg-[#060B14] border border-[#D4AF37]/20">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">{suggestion.channel} • {suggestion.best_time}</div>
                <p className="text-sm text-slate-300 mt-2">{suggestion.message}</p>
                {waNum && (
                  <a href={`https://wa.me/${waNum}?text=${encodeURIComponent(suggestion.message)}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-emerald-400 hover:underline">
                    <WhatsappLogo size={14} /> Send via WhatsApp
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-luxury p-6">
        <h3 className="font-serif text-xl text-white mb-4">Follow-ups</h3>
        <form onSubmit={addFu} className="flex flex-wrap gap-3 mb-4">
          <FollowupDateTime value={newFuDate} onChange={setNewFuDate} testidPrefix="new-fu" />
          <input placeholder="Notes…" value={newFuNotes} onChange={e => setNewFuNotes(e.target.value)} data-testid="new-fu-notes"
            className="flex-1 min-w-[200px] bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm" />
          <button data-testid="add-fu-btn" className="flex items-center gap-1 px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]"><Plus weight="bold" /> Schedule</button>
        </form>
        <div className="space-y-2">
          {followups.length === 0 && <div className="text-slate-500 text-sm">No follow-ups scheduled.</div>}
          {followups.map(f => {
            const dt = new Date(f.scheduled_at);
            const today = new Date().toDateString() === dt.toDateString();
            const overdue = !f.completed && dt < new Date() && !today;
            const badge = f.completed ? "text-emerald-300 bg-emerald-400/10 border-emerald-400/20" : overdue ? "text-rose-300 bg-rose-400/10 border-rose-400/20" : today ? "text-amber-300 bg-amber-400/10 border-amber-400/20" : "text-slate-300 bg-slate-400/10 border-slate-400/20";
            const label = f.completed ? "Done" : overdue ? "Overdue" : today ? "Today" : "Upcoming";
            return (
              <div key={f.id} className="flex items-center justify-between p-3 rounded-md bg-[#060B14] border border-[#1E293B]">
                <div>
                  <div className="text-sm text-white" data-testid="lead-fu-datetime">{formatFollowup(f.scheduled_at)}</div>
                  <div className="text-xs text-slate-400">{f.notes || "No notes"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${badge}`}>{label}</span>
                  {!f.completed && <button onClick={() => complete(f.id)} className="text-xs text-[#D4AF37] hover:underline">Mark done</button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
